import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import { MetadataActiveStatusPill } from '../../components/metadata/MetadataActiveStatusPill';
import {
  CASE_SCENARIO_TITLE_MAX_LENGTH,
  listCaseScenarioTitles,
  searchCaseScenarios,
  type CaseScenarioListRow,
} from '../../services/caseScenarioMasters';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { consumeFlashToast } from '../../utils/flashToast';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';
type StatusFilter = '' | 'active' | 'inactive';

export default function CaseScenariosPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CaseScenarioListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftTitle, setDraftTitle] = useState('');
  const [draftStatus, setDraftStatus] = useState<StatusFilter>('');

  const [title, setTitle] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [titleOptions, setTitleOptions] = useState<{ id: string; label: string }[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    setOptionsLoading(true);
    void listCaseScenarioTitles({ signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      setTitleOptions(res.success ? res.data.map((title) => ({ id: title, label: title })) : []);
      setOptionsLoading(false);
    });
    return () => {
      controller.abort();
    };
  }, [user?.id, reloadToken]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchCaseScenarios({
        page: Math.max(0, uiPage - 1),
        pageSize,
        title: title || undefined,
        status: status === '' ? undefined : status === 'active',
        sortBy: 'last_updated_at',
        sortDir: 'DESC',
      });
      if (!res.success) throw new Error(res.message);
      setRows(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
      if (uiPage > res.data.totalPages) {
        setUiPage(res.data.totalPages);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('metadata.caseScenarios.loadFailed'));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [pageSize, reloadToken, status, t, title, uiPage]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const columns = useMemo<Column<CaseScenarioListRow>[]>(() => {
    return [
      {
        key: 'title',
        header: t('metadata.caseScenarios.titleColumn'),
        getValue: (r) => r.title,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.title || EMPTY}</span>,
      },
      {
        key: 'description',
        header: t('metadata.caseScenarios.description'),
        getValue: (r) => r.description,
        render: (r) => r.description || EMPTY,
      },
      {
        key: 'active',
        header: t('common.status'),
        getValue: (r) => (r.active ? t('common.active') : t('common.inactive')),
        render: (r) => <MetadataActiveStatusPill active={r.active} />,
      },
      {
        key: 'updatedAt',
        header: t('metadata.caseScenarios.lastUpdatedAt'),
        getValue: (r) => r.updatedAt,
        render: (r) => formatDocumentTableDateTime(r.updatedAt) || EMPTY,
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/case-scenarios/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.caseScenarios.editCaseScenario')}
                aria-label={t('metadata.caseScenarios.editCaseScenario')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t]);

  const resetFilters = () => {
    setDraftTitle('');
    setDraftStatus('');
    setTitle('');
    setStatus('');
    setUiPage(1);
    setPageSize(10);
    setReloadToken((n) => n + 1);
  };

  const applyFilters = () => {
    setTitle(draftTitle);
    setStatus(draftStatus);
    setUiPage(1);
    setReloadToken((n) => n + 1);
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.caseScenarios.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.caseScenarios.title')}</div>
        </div>
        <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
          <Link to="/metadata/case-scenarios/new" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('metadata.caseScenarios.addCaseScenario')}
          </Link>
        </PermissionGate>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <LookupFieldSelect
            label={t('metadata.caseScenarios.titleColumn')}
            value={draftTitle}
            placeholder={
              optionsLoading ? t('common.loading') : t('metadata.caseScenarios.selectTitle')
            }
            options={titleOptions}
            disabled={optionsLoading}
            searchable
            maxLength={CASE_SCENARIO_TITLE_MAX_LENGTH}
            emptyMessage={t('common.noDataFound')}
            onChange={setDraftTitle}
          />
          <LookupFieldSelect
            label={t('common.status')}
            value={draftStatus}
            placeholder={t('metadata.caseScenarios.selectStatus')}
            options={[
              { id: 'active', label: t('common.active') },
              { id: 'inactive', label: t('common.inactive') },
            ]}
            onChange={(v) => setDraftStatus(v as StatusFilter)}
          />
        </div>
        <div className="flt-actions">
          <button className="flt-btn" type="button" onClick={resetFilters}>
            {t('common.reset')}
          </button>
          <button className="flt-btn pr" type="button" onClick={applyFilters}>
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.caseScenarios.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.caseScenarios.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: total,
          totalPages,
          page: uiPage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
